import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { requireCompleteProfile } from "@/lib/auth-guard";
import { useBookSearch } from "@/lib/queries";
import { searchSchema, type SearchInput } from "@/schemas/search";
import { pb } from "@/lib/pocketbase";
import {
  Collections,
  type BooksResponse,
  BooksPhaseOptions,
} from "@/types/pocketbase-types";

import BookHero from "@/components/application/books/BookHero";
import Button from "@/components/Button";
import Input from "@/components/Input";
import BookAutocomplete from "@/components/BookAutocomplete";
import LoadingSpinner from "@/components/LoadingSpinner";
import type { BookSuggestion } from "@/lib/hooks/useBookAutocomplete";

interface SearchResult {
  id: string;
  title: string;
  author: string;
  slug: string;
  phase: string;
}

const BookSearchPage = () => {
  // Get user scores for streak calculation
  // const { data: userScores = [] } = useUserScores(user?.id);

  // State for search results modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [searchError, setSearchError] = useState<string>("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchingBookId, setSearchingBookId] = useState<string | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [progressFlags, setProgressFlags] = useState({
    search_completed: false,
    metadata_downloaded: false,
    cover_downloaded: false,
    epub_downloaded: false,
    summarized: false,
    questions_generated: false,
  });

  // Helper to get phase badge styling (completed = green, active = amber pulse, pending = gray)
  const getPhaseStyle = (isCompleted: boolean, isActive: boolean) => {
    if (isCompleted) return 'bg-green-200 text-green-800';
    if (isActive) return 'bg-amber-200 text-amber-800 animate-pulse';
    return 'bg-gray-200 text-gray-600';
  };

  // Determine which phase is currently active (first incomplete in sequence)
  const getActivePhase = () => {
    if (!progressFlags.search_completed) return 'search';
    if (!progressFlags.metadata_downloaded) return 'metadata';
    if (!progressFlags.cover_downloaded) return 'cover';
    if (!progressFlags.epub_downloaded) return 'epub';
    if (!progressFlags.summarized) return 'summary';
    if (!progressFlags.questions_generated) return 'questions';
    return null; // All complete
  };

  const activePhase = getActivePhase();

  // Book search mutation
  const bookSearchMutation = useBookSearch();

  // Real-time subscription for book processing
  useEffect(() => {
    if (!searchingBookId) return;

    let unsubscribe: (() => void) | null = null;

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    // Set a timeout for the search (2 minutes - only for actual processing)
    const timeout = setTimeout(() => {
      setSearchError(
        "Search is taking longer than expected. Please try again with different search criteria."
      );
      setIsSearching(false);
      setSearchingBookId(null);
      if (unsubscribe) {
        unsubscribe();
      }
      searchTimeoutRef.current = null;
    }, 120000); // Increased to 2 minutes
    searchTimeoutRef.current = timeout;

    const setupSubscription = async () => {
      try {
        unsubscribe = await pb
          .collection(Collections.Books)
          .subscribe(searchingBookId, (data) => {
            const book = data.record as BooksResponse;
            const {
              phase,
              title,
              author,
              id,
              slug,
              search_completed,
              metadata_downloaded,
              cover_downloaded,
              epub_downloaded,
              summarized,
              questions_generated,
              error_phase,
            } = book;

            // Update progress flags
            setProgressFlags({
              search_completed: search_completed || false,
              metadata_downloaded: metadata_downloaded || false,
              cover_downloaded: cover_downloaded || false,
              epub_downloaded: epub_downloaded || false,
              summarized: summarized || false,
              questions_generated: questions_generated || false,
            });

            console.log("Unified Book Phase Update:", { phase, title });

            switch (phase) {
              case BooksPhaseOptions.searching:
                // Keep showing loading state
                break;
              case BooksPhaseOptions.searched:
                // Validate that we have actual book data
                if (!title || title.trim() === "") {
                  setSearchError(
                    "Book found but details are incomplete. Please try searching with different criteria."
                  );
                  setIsSearching(false);
                  setSearchingBookId(null);
                } else {
                  setSearchResult({
                    id,
                    title: title || "Unknown Title",
                    author: author || "Unknown Author",
                    slug: slug || id,
                    phase: phase || "unknown",
                  });
                  setIsSearching(false);
                  setSearchingBookId(null);
                }
                if (unsubscribe) {
                  unsubscribe();
                  unsubscribe = null;
                }
                // Clear timeout since search completed
                if (searchTimeoutRef.current) {
                  clearTimeout(searchTimeoutRef.current);
                  searchTimeoutRef.current = null;
                }
                break;
              case BooksPhaseOptions.error: {
                if (import.meta.env.DEV) {
                  console.error('Book processing error:', { error_phase });
                }
                setSearchError("Something went wrong. Please try again.");
                setIsSearching(false);
                setSearchingBookId(null);
                if (unsubscribe) {
                  unsubscribe();
                  unsubscribe = null;
                }
                // Clear timeout since search completed
                if (searchTimeoutRef.current) {
                  clearTimeout(searchTimeoutRef.current);
                  searchTimeoutRef.current = null;
                }
                break;
              }
            }
          });
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("Subscription error:", error);
        }
        setSearchError("Something went wrong. Please try again.");
        setIsSearching(false);
        setSearchingBookId(null);
      }
    };

    setupSubscription();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
    };
  }, [searchingBookId]);

  // Form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<SearchInput>({
    resolver: zodResolver(searchSchema),
  });

  const titleValue = watch("title");
  const authorValue = watch("author");

  // Handle book selection from autocomplete
  const handleBookSelect = (book: BookSuggestion) => {
    setValue("title", book.title);
    setValue("author", book.author);
  };

  const onSubmit = async (data: SearchInput) => {
    console.log("Unified search submitted:", data);
    console.log("On submit title: " + titleValue);
    console.log("On submit author: " + authorValue);

    setIsModalOpen(true);
    setIsSearching(true);
    setSearchError("");
    setSearchResult(null);

    try {
      const result = await bookSearchMutation.mutateAsync(data);

      if (result.isExisting) {
        // Existing processed book found - show result immediately
        console.log("Found existing processed book:", result.data.id);
        setSearchResult({
          id: result.data.id,
          title: result.data.title || result.data.user_title || "Unknown Title",
          author: result.data.author || "Unknown Author",
          slug: result.data.slug || result.data.id,
          phase: result.data.phase || "unknown",
        });
        setIsSearching(false);
        setSearchingBookId(null);
        // Clear any timeout since we have immediate result
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
          searchTimeoutRef.current = null;
        }
      } else {
        // New book created, set up subscription for real-time updates
        setSearchingBookId(result.data.id);
        console.log("Created unified book for processing:", result.data.id);
      }
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error("Unified search error:", error);
      }
      setIsSearching(false);

      if (error?.status === 403 || error?.status === 429) {
        // The server refused the request (collection rules, rate limit). Say
        // so: this case read "Something went wrong" for two months in 2026
        // while book search was dead, and nobody reported it.
        setSearchError(
          `Book search is unavailable right now (error ${error.status}). Please try again later.`
        );
      } else if (
        // ISBN not found (user-actionable hint)
        error?.message?.toLowerCase().includes("isbn") ||
        (data.isbn && !data.title && !data.author)
      ) {
        setSearchError(
          "ISBN not found. Please try searching by title and author instead."
        );
      } else {
        setSearchError("Something went wrong. Please try again.");
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSearchResult(null);
    setSearchError("");
    setIsSearching(false);
    setSearchingBookId(null);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    reset();
  };

  const searchAgain = () => {
    setSearchResult(null);
    setSearchError("");
    setIsSearching(false);
    setSearchingBookId(null);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    // Reset form to allow new search
    reset();
    // Close modal to show search form
    setIsModalOpen(false);
  };

  return (
    <>
      {/* <BooksNavbar points={totalPoints} streaks={streak} /> */}
      <BookHero title="Your Own Book" type="search" />

      <div className="container mx-auto">
        <div className="py-12 px-7 flex flex-col items-center justify-center gap-y-7">
          <div className="flex flex-col w-full">
            <h2 className="text-[32px] font-bold mb-1 color-primary">Search</h2>
            <p className="font-bold mb-5 color-primary">
              Provide either ISBN alone or Title AND Author
            </p>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-y-3"
            >
              <div className="w-full flex gap-4 flex-col lg:flex-row lg:gap-x-5">
                <BookAutocomplete
                  label="Book Name"
                  value={titleValue}
                  onSelect={handleBookSelect}
                  onChange={(value) => setValue("title", value)}
                  error={errors.title?.message}
                  onPaste={(e) => {
                    setValue("title", e.currentTarget.value, {
                      shouldValidate: true,
                    });
                  }}
                />
                <Input
                  type="text"
                  label="Author Name"
                  {...register("author")}
                  error={errors.author?.message}
                  onPaste={(e) => {
                    setValue("author", e.currentTarget.value, {
                      shouldValidate: true,
                    });
                  }}
                />
              </div>

              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-gray-400"></div>
                <span className="text-gray-700 text-sm font-bold">OR</span>
                <div className="flex-1 h-px bg-gray-400"></div>
              </div>

              <Input
                type="text"
                label="ISBN"
                {...register("isbn")}
                error={errors.isbn?.message}
                onPaste={(e) => {
                  setValue("isbn", e.currentTarget.value, {
                    shouldValidate: true,
                  });
                }}
              />

              <div className="flex flex-col gap-y-[15px] lg:w-1/2 color-primary font-semibold">
                <h3 className="text-[24px] font-bold">
                  Find your book&apos;s ISBN
                </h3>
                <ul>
                  <li>
                    1. Check the barcode at back of your book (see image).
                  </li>
                  <li>
                    2. Check the publisher information at the front of the book.
                  </li>
                  <li>
                    3. Visit{" "}
                    <a
                      href="https://isbnsearch.org"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      ISBNSearch.org
                    </a>
                    .
                  </li>
                </ul>
              </div>

              <button
                type="submit"
                className="w-fit book-search-btn"
                disabled={bookSearchMutation.isPending}
              >
                Start searching for the book
              </button>
            </form>
          </div>

          {/* ISBN Helper Card - WIP: Visual helper card for finding ISBN */}
          {/* <div className="p-7 flex items-center gap-x-7 bg-book-100 rounded-[15px]">
            <img
              src="/assets/book-card.png"
              alt="book-card"
              width={310}
              height={200}
              className="rounded-[15px] w-1/2"
            />
            <div className="flex flex-col gap-y-[15px] w-1/2">
              <h3 className="text-[24px] font-bold text-book-600">
                Find your book&apos;s ISBN
              </h3>
              <ul className="text-neutral-600">
                <li>1. Check the barcode at back of your book (see image).</li>
                <li>
                  2. Check the publisher information at the front of the book.
                </li>
                <li>
                  2. Check the publisher information at the front of the book.
                </li>
                <li>
                  3. Visit{" "}
                  <a
                    href="https://isbnsearch.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    ISBNSearch.org
                  </a>
                  .
                </li>
              </ul>
            </div>
          </div> */}
        </div>

        {/* Search Results Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center z-50 max-w-[500px] mx-auto">
            <div
              className="fixed inset-0 bg-black opacity-50"
              onClick={closeModal}
            />
            <div className="bg-neutral-100 w-full shadow-lg z-50 flex flex-col rounded-[15px] text-neutral-600 p-7 gap-y-12">
              <div className="flex flex-col">
                <h2 className="text-[24px] font-bold">Search Result</h2>
                <p className="text-xs">
                  {searchError
                    ? "Search encountered an issue"
                    : searchResult && !isSearching
                      ? searchResult.phase === "processed"
                        ? "Found processed book in our collection!"
                        : "Book found! Click 'Begin Quiz' to start processing."
                      : "Does the result match your book?"}
                </p>
              </div>

              {/* Search Result Display */}
              {searchResult && !isSearching && !searchError && (
                <>
                  <div className="p-7 flex flex-col gap-y-[5px] bg-book-200 border border-book-500 rounded-[10px]">
                    <p className="text-[32px] font-bold">
                      {searchResult.title}
                    </p>
                    <p className="text-xs">{searchResult.author}</p>
                  </div>
                  <div className="flex justify-between items-center">
                    <p
                      className="text-xs underline text-action-500 cursor-pointer"
                      onClick={searchAgain}
                    >
                      No, search again
                    </p>
                    <div className="flex flex-col items-center">
                      <a href={`/books/${searchResult.slug}`}>
                        <Button size="sm" className="w-full">
                          Begin Quiz
                        </Button>
                      </a>
                    </div>
                  </div>
                </>
              )}

              {/* Loading State */}
              {isSearching && (
                <div className="flex flex-col items-center gap-y-4">
                  <LoadingSpinner />
                  <div className="text-center">
                    <p className="text-sm font-semibold">Searching for your book...</p>
                    <p className="text-xs text-neutral-500">
                      This may take a few minutes...
                    </p>
                  </div>
                  {/* Progress Indicators */}
                  <div className="flex flex-wrap gap-2 justify-center">
                    <div className={`px-2 py-1 rounded text-xs ${getPhaseStyle(progressFlags.search_completed, activePhase === 'search')}`}>
                      📖 Search
                    </div>
                    <div className={`px-2 py-1 rounded text-xs ${getPhaseStyle(progressFlags.metadata_downloaded, activePhase === 'metadata')}`}>
                      📝 Metadata
                    </div>
                    <div className={`px-2 py-1 rounded text-xs ${getPhaseStyle(progressFlags.cover_downloaded, activePhase === 'cover')}`}>
                      🖼️ Cover
                    </div>
                    <div className={`px-2 py-1 rounded text-xs ${getPhaseStyle(progressFlags.epub_downloaded, activePhase === 'epub')}`}>
                      📚 EPUB
                    </div>
                    <div className={`px-2 py-1 rounded text-xs ${getPhaseStyle(progressFlags.summarized, activePhase === 'summary')}`}>
                      📄 Summary
                    </div>
                    <div className={`px-2 py-1 rounded text-xs ${getPhaseStyle(progressFlags.questions_generated, activePhase === 'questions')}`}>
                      ❓ Questions
                    </div>
                  </div>
                </div>
              )}

              {/* Error State */}
              {searchError && !isSearching && !searchResult && (
                <div className="flex flex-col gap-y-4">
                  <p className="text-xs text-red-500">{searchError}</p>
                  <div className="flex gap-x-3 self-center">
                    <Button onClick={closeModal} size="sm" variant="outline">
                      Close
                    </Button>
                    <Button onClick={searchAgain} size="sm">
                      Try Again
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export const Route = createFileRoute("/books/search")({
  ...requireCompleteProfile(),
  component: BookSearchPage,
});
